import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { LabelId, UserCommunication } from '@this+that/models';
import log from 'loglevel';

import { useDeleteDraft } from './useDeleteDraft';
import {
  useMessageDialogStore,
  useUserCommunicationsStore,
} from '../../../store';
import { resolveDraftCommunication } from '../../../utils/userCommunicationHelpers';
import { useCustomToast } from '../../useCustomToast';

export class BulkDeleteDraftsError extends Error {
  readonly failed: number;
  readonly total: number;

  constructor(failed: number, total: number) {
    super(`Failed to delete ${failed} of ${total} drafts`);
    this.name = 'BulkDeleteDraftsError';
    this.failed = failed;
    this.total = total;
  }
}

export const useBulkDeleteDrafts = () => {
  const { t } = useTranslation();
  const { deleteDraft } = useDeleteDraft();

  const { setDeleteLoading, getUserCommunicationByMessageId } =
    useUserCommunicationsStore();
  const { errorMessage, promiseToast } = useCustomToast();

  const handleBulkDeleteDrafts = useCallback(
    async (messageIds: string[] | string, onSuccess?: () => void) => {
      const messageIdsArray = Array.isArray(messageIds)
        ? messageIds
        : [messageIds];

      if (messageIdsArray.length === 0) return;

      const draftsToDelete = messageIdsArray
        .map((messageId) => {
          const userCommunication = getUserCommunicationByMessageId(messageId);

          if (!userCommunication) {
            log.error(
              `UserCommunication with messageId ${messageId} not found`,
            );
            return null;
          }

          if (!userCommunication.labelIds?.includes(LabelId.DRAFT)) {
            log.warn(
              `UserCommunication with messageId ${messageId} is not a draft`,
            );
            return null;
          }

          const resolvedDraftId =
            resolveDraftCommunication(userCommunication)?.draftId;

          if (!resolvedDraftId) {
            log.error(
              `UserCommunication with messageId ${messageId} has no draftId`,
            );
            return null;
          }

          return {
            draftId: resolvedDraftId,
            communication: userCommunication,
          };
        })
        .filter(
          (
            item,
          ): item is { draftId: string; communication: UserCommunication } =>
            item !== null,
        );

      if (draftsToDelete.length === 0) {
        errorMessage(t('inbox.userCommunication.actions.deleteDraftError'));
        return;
      }

      const closeIfShowingDraftPromise = useMessageDialogStore
        .getState()
        .closeIfShowingDraft(draftsToDelete.map(({ draftId }) => draftId));

      onSuccess?.();
      setDeleteLoading(true);

      const total = draftsToDelete.length;
      const loadingMessage =
        total === 1
          ? t('inbox.userCommunication.actions.deletingDraft')
          : t('inbox.userCommunication.actions.deletingDrafts', {
              count: total,
            });
      const successMessageText =
        total === 1
          ? t('inbox.userCommunication.actions.deleteDraftSuccess')
          : t('inbox.userCommunication.actions.deleteDraftMultipleSuccess', {
              count: total,
            });

      const runDeletes = async (): Promise<void> => {
        try {
          await closeIfShowingDraftPromise;
          const results = await Promise.all(
            draftsToDelete.map(async ({ draftId, communication }) => {
              try {
                const success = await deleteDraft(draftId, {
                  removeFromStore: true,
                });

                if (!success) {
                  throw new Error('Failed to delete draft on the platform');
                }

                return { draftId, success: true, communication };
              } catch (error) {
                log.error(
                  `Failed to delete draft with draftId ${draftId}:`,
                  error,
                );
                return { draftId, success: false, communication, error };
              }
            }),
          );

          const failed = results.filter((r) => !r.success);

          if (failed.length > 0) {
            log.warn(
              `${failed.length} failures while deleting drafts out of ${total} operations`,
            );

            throw new BulkDeleteDraftsError(failed.length, total);
          }
        } finally {
          setDeleteLoading(false);
        }
      };

      const deletePromise = runDeletes();

      promiseToast(
        deletePromise,
        loadingMessage,
        successMessageText,
        (error) =>
          error instanceof BulkDeleteDraftsError && error.failed < error.total
            ? t('inbox.userCommunication.actions.partialDeleteDraftError', {
                failed: error.failed,
                total: error.total,
              })
            : t('inbox.userCommunication.actions.deleteDraftError'),
      );

      try {
        await deletePromise;
      } catch (error) {
        log.error('Error while bulk deleting drafts:', error);
      }
    },
    [deleteDraft, setDeleteLoading, promiseToast, errorMessage, t],
  );

  return { handleBulkDeleteDrafts };
};
